import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setAccessToken} from "../slices/authSlice";
import {getContent, getContentList} from "../api/client";

import {Header} from "../components/Header";
import {CenteredSpinner, CenteredSpinner2} from "../components/CenteredSpinner";
import {setQueryString} from "../slices/usersSlice";
import {HumanDate, HumanDuration} from "../components/Helpers";
import {Customer, SeismicEmployee} from "../components/Utils";


const Sentence = ({sentence}) => {
    return <tr><td style={{whiteSpace: "nowrap"}}>{sentence.isInternalUser ? <SeismicEmployee /> : <Customer/>} {sentence.speaker}</td><td>{sentence.sentence}</td></tr>
}

const Question = ({question}) => {
    return <tr><td style={{whiteSpace: "nowrap"}}>{question.isInternalUser ? <SeismicEmployee /> : <Customer/>} {question.speaker}</td><td>{question.question}</td></tr>
}

const Meeting = ({meeting}) => {

    var link =`/Meeting?id=${meeting.id}`;
    var externalLink = null;
    
    if(meeting?.externalMeetingId != null)
        externalLink = <a target="blank" href={`/MeetingRedirect?externalMeetingId=${encodeURIComponent(meeting.externalMeetingId)}`}> [Detail UI] </a>;
    
    var meetingElement = <>
        <u>{meeting?.presentedByName}</u> presented this during <a target="_blank" href={link}>{meeting.title}</a> {externalLink} for <i>
        <b>{<HumanDuration duration={meeting.pageDurationSeconds * 1000} />}</b> (<HumanDate date={meeting.meetingStartTime}/>)</i>
    </>
    
    if(meeting.sentences.length === 0 && meeting.questions.length === 0)
        return <div>
            <li>{meetingElement}</li>
        </div>
    return <div>
        <details>
            <summary>{meetingElement}</summary>
            <p style={{paddingLeft: 24}}>
            {meeting.sentences.length > 0 && <>
            <h5>Discussion</h5>
                <table>
                    {meeting.sentences.map(a=><Sentence sentence={a} />)}                    
                </table>
            </>}
            {meeting.questions.length > 0 && <>
                <h5>Questions</h5>
                <table>
                    {meeting.questions.map(a=><Question question={a} />)}
                </table>
            </>}
            </p>
        </details>
    </div>
}

const TopPresenter = ({presenter}) => {
    return <li>{presenter.name} has shown this <b>{presenter.numberOfTimes}</b> times for a total of <b>{<HumanDuration duration={presenter.totalDurationSeconds * 1000}/>}</b> ({<HumanDuration duration={presenter.averageDurationSeconds.toFixed(0) * 1000}/>} on average).</li>
}

const Account = ({account}) => {
    return <li>{account.name}</li>
}

const Opportunity = ({opportunity}) => {
    return <li>{opportunity.name}</li>
}

const Page = ({page}) => {
    return <div style={{paddingBottom: 16}}>
        <div style={{border: "1px solid #cfd4da", padding: "16px", backgroundColor: "white", borderRadius: 4}}>
            <h2 style={{paddingBottom: 16}}>Page {page.index}</h2>
            <div style={{display: "flex", justifyContent: "space-between"}}>
                <div style={{width: "50%", paddingRight: 24}}>
                    <img style={{border: "1px solid #868e96", width: "100%"}} alt="slide thumbnail" src={page.thumbnailImageUrl} />
                </div>
                <div style={{width: "50%"}}>
                    <PageInsights page={page} />
                    <h4>Discussion Summary</h4>
                    <p>{page.discussionSummary}</p>
                    <h4>Common Questions</h4>
                    <p>{page.commonQuestions}</p>
                    <h4>Top Presenters</h4>
                    <p>{page.topPresenters.map(a=><TopPresenter presenter={a} />)}</p>
                    {page.accounts.length > 0 && <>
                        <h4>Recent Accounts</h4>
                        <p>{page.accounts.map(a=><Account account={a} />)}</p>
                    </>}
                    {page.opportunities.length > 0 && <>
                        <h4>Recent Opportunities</h4>
                        <p>{page.opportunities.map(a=><Opportunity opportunity={a} />)}</p>
                    </>}
                </div>
            </div>
            <div style={{paddingTop: 16}}>
                <h4>Recent Discussions</h4>
                <p>{page.meetings.map(a=><Meeting meeting={a} />)}</p>
            </div>
        </div>
    </div>

}

const PageInsights = ({page}) => {
    return <div>
        <h4>Page Insights</h4>
        <p>
            <li>This content has been used in <b>{page.numberOfDistinctMeetings}</b> meeting(s) and was last presented <b><HumanDate date={page.lastPresentedAt} /></b></li>
            <li>On average it takes about <b><HumanDuration duration={page.averageTimeSpentOnPage * 1000} /></b> to show this page.</li>
            {page.sellerTalkPercentage > 0 && <li>Sellers are talking <b>{page.sellerTalkPercentage}%</b> of the time this page is being viewed.</li>}
            {page.buyerTalkPercentage > 0 && <li>Buyers are talking <b>{page.buyerTalkPercentage}%</b> of the time this page is being viewed.</li>}
            {(page.otherTalkPercentage > 0 && page.otherTalkPercentage !== 100) && <li>Nobody is talking <b>{page.otherTalkPercentage}%</b> of the time this page is being viewed.</li>}
            {page.industries.length > 0 && <li>Industries: <b>{page.industries.map(a=>a.industry + " (" + a.count + ")").join(", ")}</b></li>}
        </p>
    </div>
}

const Insights = ({content}) => {
    return <div style={{paddingBottom: 16}}>
        <div style={{backgroundColor: "white", border: "1px solid #cfd4da", borderRadius: 4, padding: 16}}>
            <h4><a target="_blank" href={`https://quake.seismic.com/app/#/content/${content.contentId}`}>{content.name}</a> Insights</h4>
            <p>
                <li>This content has been used in <b>{content.numberOfDistinctMeetings}</b> meeting(s).</li>
                <li>On average it takes about <b><HumanDuration duration={content.averageTimeSpentOnDeck * 1000} /></b> to show this deck.</li>
                {content.industries.length > 0 && <li>Industries: <b>{content.industries.map(a=>a.industry + " (" + a.count + ")").join(", ")}</b></li>}
            </p>
        </div>
    </div>
}

export const Content = () => {

    const urlParams = new URLSearchParams(window.location.search);
    const qsContentId = urlParams.get('originLibraryContentVersionId');
    const dispatch = useDispatch();
    const [contentList, setContentList] = useState([]);
    const [selectedContentId, setSelectedContentId] = useState(null);
    const [content, setContent] = useState(null);
    
    useEffect(() => {
        getContentList()
            .then(data => {
                setContentList(data);
                if(data.filter(a=>a.id === qsContentId).length > 0)
                    setSelectedContentId(qsContentId);
                else
                    setSelectedContentId(data[0].id);
            })
    }, []);

    useEffect(() => {
        if (selectedContentId === null)
            return;
        setContent(null);
        getContent(selectedContentId)
            .then(data => {
                setContent(data)
            })
    }, [selectedContentId]);

    const signOut = () => {
        dispatch(setAccessToken(null));
    }

    return <>
        <Header signOut={signOut} title="Seismic Meetings - Content Usage" navigateTo="/Content" />
        <div style={{padding: "16px"}}>
            <div style={{paddingBottom: 16}}>
                <select value={selectedContentId} onChange={(e) => {
                    setQueryString("originLibraryContentVersionId", e.target.value);
                    setSelectedContentId(e.target.value)
                }
                }>
                    {contentList.map(item => (
                        <option key={item.id} value={item.id}>
                            {item.name} - {item.format} used in {item.count} meetings
                        </option>
                    ))}
                </select>
            </div>
            {content === null && <CenteredSpinner />}
            {content && <div>
                <Insights content={content} />
                {content.pages.map(page => <Page key={page.index} page={page} />)}
            </div>}
        </div>
    </>;
}